import * as React from 'react'
import dynamic from 'next/dynamic'
import {motion, useMotionValue, useTransform} from 'framer-motion'
import debounce from 'lodash/debounce'

import Header from '@components/demo/Header'
import Footer from '@components/demo/Footer'
import Contact from '@components/demo/Contact'
import ImgSet from '@components/demo/ImgSet'
import UploadForm from '@components/demo/UploadForm'
import UploadResult from '@components/demo/UploadResult'
import CTAButton from '@components/demo/CTAButton'

import {t, axios} from '@lib/helpers'

const Meta = dynamic(() => import('@components/Meta'))

const Page = ({isRecaptchaValidated}) => {
  return (
    <React.Fragment>
      <Meta title={`WowOCR – ${t('layout.title')}`} />

      <div className='DemoDetail OCR'>
        <Header name='WowOCR' />

        <div className='DemoDetail__wrapper'>
          <Hero />
          <Why />
        </div>

        <Upload isRecaptchaValidated={isRecaptchaValidated} />

        <Contact isRecaptchaValidated={isRecaptchaValidated} />

        <Footer />
      </div>
    </React.Fragment>
  )
}

const cardProps = {
  idCard: {
    scanCard: {
      amount: 4,
      hilightClassName: 'hilight__id-card',
    },
    img: {
      src: '/images/demo/ocr/id-card.png',
      alt: 'ID Card',
    },
  },
  driver: {
    scanCard: {
      amount: 5,
      hilightClassName: 'hilight__driver',
    },
    img: {
      src: '/images/demo/ocr/drivers-license.png',
      alt: 'Drivers License',
    },
  },
  passport: {
    scanCard: {
      amount: 8,
      hilightClassName: 'hilight__passport',
    },
    img: {
      src: '/images/demo/ocr/passport.png',
      alt: 'Passport',
    },
  },
}

const Hero = () => {
  const [order, setOrder] = React.useState(['driver', 'idCard', 'passport'])

  const next = () =>
    setOrder((array) => [...array.slice(-1), ...array.slice(0, -1)])
  const previous = () =>
    setOrder((array) => [...array.slice(1), ...array.slice(0, 1)])

  // middle
  const {img, scanCard} = cardProps[order[1]]

  return (
    <section className='DemoDetail__hero'>
      <div className='DemoDetail__feature'>
        <img
          {...cardProps[order[0]].img}
          className='DemoDetail__feature__img'
        />
        <button onClick={previous}>
          <ImgSet src='/images/demo/ocr/previous.png' alt='Previous' />
        </button>
        <ScanCard key={order[1]} {...scanCard}>
          <ImgSet {...img} />
        </ScanCard>
        <button onClick={next}>
          <ImgSet src='/images/demo/ocr/next.png' alt='Next' />
        </button>
        <img
          {...cardProps[order[2]].img}
          className='DemoDetail__feature__img'
        />
      </div>

      <div className='container'>
        <div className='DemoDetail__hero__content'>
          <h1 className='DemoDetail__title DemoDetail__title--hilight'>
            {t('pages.demo.ocr.hero.title')}
          </h1>
          <h4>{t('pages.demo.ocr.hero.subtitle')}</h4>
          <CTAButton />
          <p>{t('pages.demo.ocr.hero.description')}</p>
        </div>
      </div>
    </section>
  )
}

const ScanCard = ({children, hilightClassName, amount}) => {
  const constraintsRef = React.useRef(null)
  const [height, setHeight] = React.useState(0)

  const y = useMotionValue(0)
  const inputRange = [(-1 * height) / 2, 0, height / 2]
  const translateY = useTransform(y, inputRange, ['100%', '50%', '0%'])
  const invertTranslateY = useTransform(y, inputRange, ['-100%', '-50%', '0%'])

  React.useEffect(() => {
    let mount = true
    const handler = () => {
      if (mount) {
        const bounding = constraintsRef.current.getBoundingClientRect()
        setHeight(Math.round(bounding.height))
      }
    }
    const debouncedHandler = debounce(handler, 300)

    handler()
    window.addEventListener('resize', debouncedHandler)

    return () => {
      mount = false
      window.removeEventListener('resize', debouncedHandler)
    }
  }, [])

  return (
    <div className='OCR__scan' ref={constraintsRef}>
      <img
        src='/images/demo/ocr/scan-edge.png'
        srcSet='/images/demo/ocr/scan-edge@2x.png 2x, /images/demo/ocr/scan-edge@3x.png 3x'
        alt='Frame'
        loading='lazy'
      />

      <div className='OCR__scan__card'>{children}</div>

      <div className='OCR__scan__mask'>
        <motion.div
          className='OCR__scan__mask__outer'
          style={{y: invertTranslateY}}
        >
          <motion.div
            className='OCR__scan__mask__inner'
            style={{y: translateY}}
          >
            {Array.from({length: amount}).map((_, index) => (
              <div
                key={`${hilightClassName}-${index}`}
                className={hilightClassName}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className='OCR__scan__handle'
        drag='y'
        style={{y}}
        dragElastic={false}
        dragMomentum={false}
        dragConstraints={constraintsRef}
      />
    </div>
  )
}

const Why = () => {
  return (
    <section className='DemoDetail__why'>
      <div className='container' id='why'>
        <div className='DemoDetail__why__img'>
          <img
            src='/images/demo/ocr/why.jpg'
            srcSet='/images/demo/ocr/why@2x.jpg 2x, /images/demo/ocr/why@3x.jpg 3x'
            alt='WowOCR'
          />
        </div>

        <div className='DemoDetail__why__content'>
          <h2
            className='DemoDetail__title DemoDetail__title--hilight'
            dangerouslySetInnerHTML={{
              __html: t('pages.demo.ocr.why.title'),
            }}
          />
          <p>{t('pages.demo.ocr.why.description')}</p>
        </div>
      </div>
    </section>
  )
}

const Upload = ({isRecaptchaValidated}) => {
  const [extractor, setExtractor] = React.useState('id_card')
  const [result, setResult] = React.useState('')

  const types = [
    {
      label: t('pages.demo.ocr.type.id_card'),
      value: 'id_card',
    },
    {
      label: t('pages.demo.ocr.type.driver_license'),
      value: 'driver_license',
    },
    {
      label: t('pages.demo.ocr.type.passport'),
      value: 'passport',
    },
  ]

  const handleChangeType = (event) => setExtractor(event.target.value)

  const handleChange = async (file) => {
    const formData = new FormData()
    formData.append('extractor', extractor)
    formData.append('image', file)

    try {
      const response = await axios.post('/demo/ocrs', formData, {
        headers: {
          'X-Api-Key': 'Data wow',
        },
      })
      setResult(JSON.stringify(response.data.data.results, null, 2))
    } catch (err) {
      setResult('Something wrong please try again.')
    }
  }

  return (
    <section className='DemoDetail__upload' id='upload'>
      <header>
        <div className='container'>
          <h2 className='DemoDetail__title'>OCR</h2>
          <h4>{t('pages.demo.ocr.upload.subtitle')}</h4>
          <div className='DemoDetail__upload__types'>
            {types.map(({label, value}, index) => (
              <label key={index}>
                <input
                  type='radio'
                  name='type'
                  value={value}
                  onChange={handleChangeType}
                  defaultChecked={index === 0 && true}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </header>

      <hr />

      <div className='DemoDetail__upload__content'>
        <UploadForm
          name='WowOCR'
          onChange={handleChange}
          isLoading={isRecaptchaValidated}
        />

        <UploadResult result={result} />
      </div>
    </section>
  )
}

export default Page
